import { useQuery } from '@apollo/client';
import { Divider, Header, Loader, Tabs, Text } from '@gasbuddy/react-components';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import OutageHistory from './OutageHistory';

export const GET_HISTORY = gql`
  query GetHistory($id: ID!, $limit: Int) {
    history(id: $id, limit: $limit) {
      hasDiesel {
        imageUrl
        nickname
        reportStatus
        stamp
        updateDate
      }
      hasGas {
        imageUrl
        nickname
        reportStatus
        stamp
        updateDate
      }
      hasPower {
        imageUrl
        nickname
        reportStatus
        stamp
        updateDate
      }
    }
  }
`;

export default function StationStatusHistory({
  includeDiesel,
  stationId,
}) {
  const { t } = useTranslation('station');
  const { data, error: dataError, loading } = useQuery(GET_HISTORY, {
    variables: {
      id: stationId,
    },
  });
  const { history } = data || {};

  const tabs = [
    { type: 'hasPower', title: t('outage.power', { ns: 'station' }) },
    { type: 'hasGas', title: t('outage.gas', { ns: 'station' }) },
  ];
  if (includeDiesel) {
    tabs.push({ type: 'hasDiesel', title: t('outage.diesel', { ns: 'station' }) });
  }

  let innerContent;

  if (loading) {
    innerContent = <Loader />;
  } else if (dataError) {
    innerContent = (
      <Text as="p" color="orange">
        {t('outage.errorRetrievingOutageHistory', { ns: 'station' })}
      </Text>
    );
  } else if (!history || !Object.values(history).length) {
    innerContent = (
      <Text as="p" color="orange">
        {t('outage.noRecentHistory', { ns: 'station' })}
      </Text>
    );
  } else {
    innerContent = (
      <Tabs
        fluid
        color="white"
      >
        {tabs.map((tab, index) => (
          <Tabs.Item
            fluid
            title={tab.title}
            key={tab.type}
            tabIndex={index}
          >
            <OutageHistory titleKey={tab.type} list={history[tab.type]} />
          </Tabs.Item>
        ))}
      </Tabs>
    );
  }

  return (
    <Fragment>
      <Header as="h3">
        {t('outage.stationStatusHistory', { ns: 'station' })}
      </Header>
      <Divider />
      {innerContent}
    </Fragment>
  );
}

StationStatusHistory.propTypes = {
  includeDiesel: PropTypes.bool,
  stationId: PropTypes.string.isRequired,
};

StationStatusHistory.defaultProps = {
  includeDiesel: false,
};
